/**
 * ============================================================================
 *  EnterpriseHero3D — reusable premium hero with a real Three.js Earth.
 * ============================================================================
 *  Public component consumed by CinematicHero (and any other marketing page).
 *  Accepts headline / description / CTA / stat props so it can be reused
 *  without code duplication.
 * ============================================================================
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Rocket, ShieldCheck, Cpu, MapPin } from "lucide-react";
import EarthScene from "./EarthScene";

const DEFAULT_STATS = [
  { icon: Rocket,      big: "60s",    small: "Instant Deployment" },
  { icon: ShieldCheck, big: "99.99%", small: "Uptime Guarantee"   },
  { icon: Cpu,         big: "NVMe",   small: "Blazing Fast SSD"   },
];

export default function EnterpriseHero3D({
  eyebrow      = "Next Gen Cloud Infrastructure",
  title,
  description  = "Ultra-low latency VPS hosting platform built for speed, security and scalability. Deploy in 60 seconds across the globe with enterprise-grade reliability and premium NVMe networking.",
  primaryCTA   = { label: "Deploy Your Server",     to: "/register",  testId: "enterprise-hero-cta-primary"   },
  secondaryCTA = { label: "View Global Locations",  to: "/locations", testId: "enterprise-hero-cta-secondary" },
  stats        = DEFAULT_STATS,
  className    = "",
  onLocationChange,
} = {}) {
  return (
    <section
      className={`enterprise-hero-3d relative overflow-hidden pt-24 pb-16 md:pt-28 md:pb-24 ${className}`}
      data-testid="enterprise-hero-3d"
    >
      {/* Nebula wash — purple/pink only, no blue */}
      <div className="enterprise-hero-3d__nebula-a" aria-hidden />
      <div className="enterprise-hero-3d__nebula-b" aria-hidden />

      {/*
        Cinematic Earth layer — absolutely positioned, NOT a grid cell.
        On desktop it floats over the right ~70% of the hero and bleeds
        beyond every edge; on mobile it snaps below the copy via the spacer.
      */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="hero-earth-layer"
        data-testid="hero-earth-layer"
        aria-hidden="true"
      >
        <EarthScene className="hero-earth-canvas" onLocationChange={onLocationChange} />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-8 lg:gap-12 items-center pointer-events-none">
        {/* ---------- LEFT: copy ---------- */}
        <div className="relative pointer-events-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-[11px] font-semibold tracking-[0.28em] uppercase text-primary/90"
          >
            {eyebrow}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
            className="mt-5 font-display font-black tracking-[-0.035em] leading-[0.95] text-4xl sm:text-5xl lg:text-6xl xl:text-[4.75rem]"
          >
            {title ?? (
              <>
                <span className="block">Servers.</span>
                <span className="block">Deployed.</span>
                <span className="block brand-gradient-text">Everywhere.</span>
              </>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="mt-6 max-w-xl text-sm sm:text-base text-muted-foreground leading-relaxed"
          >
            {description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.32 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to={primaryCTA.to}
              data-testid={primaryCTA.testId}
              className="btn-primary px-6 py-3 text-sm shadow-[0_10px_40px_-8px_rgba(139,92,246,0.55)] hover:shadow-[0_18px_60px_-6px_rgba(236,72,153,0.55)] transition-shadow"
            >
              {primaryCTA.label} <ArrowUpRight size={16} />
            </Link>
            <Link
              to={secondaryCTA.to}
              data-testid={secondaryCTA.testId}
              className="btn-outline px-6 py-3 text-sm"
            >
              <MapPin size={14} /> {secondaryCTA.label}
            </Link>
          </motion.div>

          <motion.div
            initial="hidden" animate="show"
            variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.5 } } }}
            className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-md"
          >
            {stats.map((s) => (
              <motion.div
                key={s.small}
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-4 hover:border-primary/40 transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-primary/12 border border-primary/25 flex items-center justify-center">
                  <s.icon size={14} className="text-primary" />
                </div>
                <div className="mt-3 font-display text-2xl font-black tracking-tight">{s.big}</div>
                <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground leading-tight">{s.small}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ---------- RIGHT: mobile-only spacer to reserve stacking room ---------- */}
        <div className="hero-earth-spacer lg:hidden" aria-hidden />
      </div>

      {/* Local styles for this hero only — no global theme changes. */}
      <HeroStyles />
    </section>
  );
}

/* -------------------------------------------------------------------------
 *  Scoped stylesheet — every rule keyed to .enterprise-hero-3d, so nothing
 *  can leak into the rest of the app.
 * ----------------------------------------------------------------------- */
function HeroStyles() {
  return (
    <style>{`
      .enterprise-hero-3d { isolation: isolate; }

      .enterprise-hero-3d__nebula-a,
      .enterprise-hero-3d__nebula-b {
        position: absolute; inset: auto;
        border-radius: 9999px; pointer-events: none; z-index: 0;
        filter: blur(80px);
      }
      .enterprise-hero-3d__nebula-a {
        left: -140px; top: 40px; width: 560px; height: 560px;
        background: radial-gradient(circle, rgba(124,58,237,0.28), transparent 62%);
      }
      .enterprise-hero-3d__nebula-b {
        right: -160px; top: 20px; width: 620px; height: 620px;
        background: radial-gradient(circle, rgba(236,72,153,0.22), transparent 62%);
      }

      /* Base wrapper — position controlled via the passed className. */
      .earth-canvas-wrap {
        cursor: grab;
        touch-action: pan-y;   /* let vertical scroll pass through */
      }
      .earth-canvas-wrap:active { cursor: grabbing; }

      /* -------------------------------------------------------------------
         CINEMATIC EARTH LAYER
         The Earth is NOT a grid cell — it is an absolutely-positioned
         cinematic background object that floats over the hero. Section-level
         overflow-hidden still confines the visual bleed to the hero, so
         nothing spills into the sections above or below.
         Mobile: full-width layer that stacks below the copy.
         Desktop: floats over the right ~70% and bleeds off every edge.
         ------------------------------------------------------------------- */
      .hero-earth-layer {
        position: absolute;
        top: auto;
        right: 0;
        bottom: 0;
        left: 0;
        height: 460px;
        width: 100%;
        z-index: 1;
        overflow: visible;
        pointer-events: auto;
      }
      .hero-earth-canvas {
        position: absolute !important;
        inset: 0 !important;
        overflow: visible !important;
      }
      .hero-earth-canvas > canvas {
        background: transparent !important;
        display: block;
      }
      /* Reserve stacking room on mobile only (grid item, not the earth) */
      .hero-earth-spacer {
        height: 440px;
        width: 100%;
      }
      @media (min-width: 640px) {
        .hero-earth-layer  { height: 560px; }
        .hero-earth-spacer { height: 540px; }
      }
      @media (min-width: 1024px) {
        /* Desktop: cinematic float — 70% of hero width, bleeds every edge.
           Section overflow-hidden clips at section boundary only, so glow
           stays inside the hero but is never clipped by the earth's own box. */
        .hero-earth-layer {
          top: -100px;
          bottom: -100px;
          left: 30%;
          right: -8%;
          width: auto;
          height: auto;
        }
      }
      @media (min-width: 1280px) {
        .hero-earth-layer {
          top: -120px;
          bottom: -120px;
          left: 32%;
          right: -6%;
        }
      }
      @media (min-width: 1600px) {
        .hero-earth-layer {
          top: -140px;
          bottom: -140px;
          left: 34%;
          right: -4%;
        }
      }

      /* Floating glass labels rendered by drei's <Html/>. */
      .earth-label {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 5px 10px;
        border-radius: 10px;
        background: rgba(10, 8, 23, 0.72);
        border: 1px solid rgba(168, 85, 247, 0.28);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        box-shadow: 0 8px 28px -8px rgba(0,0,0,0.9),
                    0 0 22px -6px rgba(168, 85, 247, 0.45);
        color: #f4eeff;
        font-family: inherit;
        font: inherit; color: #f4eeff;
        white-space: nowrap;
        cursor: pointer;
        transform: translateZ(0);
        transition: transform .35s cubic-bezier(0.16,1,0.3,1),
                    box-shadow .35s cubic-bezier(0.16,1,0.3,1),
                    border-color .35s cubic-bezier(0.16,1,0.3,1),
                    background .35s cubic-bezier(0.16,1,0.3,1);
      }
      .earth-label[data-hover="true"] {
        transform: translate3d(0, -3px, 0) scale(1.06);
        border-color: rgba(236, 72, 153, 0.6);
        box-shadow: 0 12px 32px -8px rgba(0,0,0,0.9),
                    0 0 36px -4px rgba(236, 72, 153, 0.55);
      }
      .earth-label[data-active="true"] {
        transform: translate3d(0, -5px, 0) scale(1.12);
        background: rgba(30, 10, 45, 0.85);
        border-color: rgba(236, 72, 153, 0.85);
        box-shadow: 0 14px 40px -8px rgba(0,0,0,0.95),
                    0 0 48px -2px rgba(236, 72, 153, 0.75),
                    0 0 24px -4px rgba(168, 85, 247, 0.7);
      }
      .earth-label[data-active="true"] .earth-label__city {
        color: #ffffff;
      }
      .earth-label[data-active="true"] .earth-label__country {
        color: rgba(244, 114, 182, 0.95);
      }
      .earth-label__flag  { font-size: 13px; line-height: 1; }
      .earth-label__stack { display: flex; flex-direction: column; line-height: 1.1; text-align: left; }
      .earth-label__city    { font-size: 11.5px; font-weight: 700; letter-spacing: 0.01em; color: #f7f0ff; }
      .earth-label__country { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(196, 132, 252, 0.85); }
    `}</style>
  );
}
